import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { eachDayOfInterval, endOfMonth, startOfMonth } from 'date-fns';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import { PageShell } from '../../../components/leaves/PageShell';
import { Calendar } from 'lucide-react';
import AttendanceCalendar from './components/AttendanceCalendar';
import LeaveRequestPanel from './components/LeaveRequestPanel';
import WorkScheduleCalendar from './components/WorkScheduleCalendar';
import DetailedDayViewPanel from './components/DetailedDayViewPanel';
import { appDate } from '../../../utils/appDateFormat';
import { Skeleton } from '../../../components/ui/Skeleton';

export { WorkScheduleCalendar };

// Month-grid placeholder shown while attendance data is loading for the first time.
// Mirrors AttendanceCalendar's 7-col header + 6-week grid layout so the page
// doesn't reflow once data arrives.
const CalendarSkeleton = () => (
  <div className="w-full" aria-busy="true" aria-live="polite">
    <div className="flex items-center justify-between mb-6">
      <Skeleton className="h-8 w-8 rounded-lg" />
      <Skeleton className="h-6 w-40 rounded" />
      <Skeleton className="h-8 w-8 rounded-lg" />
    </div>
    <div className="grid grid-cols-7 gap-1">
      {Array.from({ length: 7 }).map((_, i) => (
        <Skeleton key={`hdr-${i}`} className="h-4 w-10 mx-auto rounded" />
      ))}
      {Array.from({ length: 42 }).map((_, i) => (
        <Skeleton key={`cell-${i}`} className="h-12 w-full rounded-lg" />
      ))}
    </div>
    <div className="mt-6 pt-4 border-t border-border">
      <Skeleton className="h-3 w-16 mb-3 rounded" />
      <div className="flex flex-wrap gap-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={`legend-${i}`} className="flex items-center gap-2">
            <Skeleton className="h-4 w-4 rounded-full" />
            <Skeleton className="h-3 w-16 rounded" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

import { Roles } from '../../../components/roles';
import { formatLocalDateParam } from '../../../utils/apiDate';
import { getTimesheetTableData } from '../../../api/Attendance/attendance';
import { useScheduledShifts } from '../../../hooks/useScheduling';
import { getMyCorrectionRequests } from '../../../api/Attendance/correctionRequests';
import { getUserManagers } from '../../../api/General/account';
import { getCompanyRules } from '../../../api/General/Company';

// ── Day-status vocabulary ────────────────────────────────────────────────────
// A day carries two facts that can disagree: what the roster nominally says
// (the backend's DayStatus) and what the employee actually did (clockings).
// These sets make the difference explicit so every consumer on this page —
// the calendar cell, the day panel and the KPI strip — reads the same rules.

// Statuses that mean "the employee worked this day".
const WORKED_STATUSES = new Set(['present', 'late', 'early-leave', 'half-day']);
// Statuses that count towards the Present Days tile (half-day is counted
// separately, at half weight, so it is deliberately not here).
const PRESENT_STATUSES = new Set(['present', 'late', 'early-leave']);
// Statuses that mean "expected at work and not there".
const ABSENT_STATUSES = new Set(['absent', 'leave', 'sick-leave']);
// Statuses the company never expected anyone to work.
const NON_WORKING_STATUSES = new Set(['week-off', 'holiday']);

const AttendanceManagement = ({
  targetUserId: targetUserIdProp,
  targetUserFullName,
} = {}) => {
  const navigate = useNavigate();
  const params = useParams();
  const { t } = useTranslation();

  const targetUserIdFromRoute = params?.targetUserId;
  const targetUserFullNameFromRoute = (() => {
    const raw = params?.targetUserFullName;
    if (!raw) return undefined;
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  })();

  const viewerUserId = useSelector((state) => state.user.userId);
  const viewerRole = useSelector((state) => state.user.role);
  const viewerCompanyId = useSelector((state) => state.user.company?.id);
  const rtl = useSelector((state) => state.user.rtl);

  const titleIconGapClass = rtl ? 'ml-2 mr-0' : 'mr-2 ml-0';
  const headerIconGapClass = rtl ? 'ml-4 mr-0' : 'mr-4 ml-0';
  const inlineSpaceClass = rtl ? 'space-x-2 space-x-reverse' : 'space-x-2';

  const targetUserId = targetUserIdProp || targetUserIdFromRoute || viewerUserId;
  const effectiveTargetUserFullName = targetUserFullName || targetUserFullNameFromRoute;
  const isSelf = !!targetUserId && !!viewerUserId && targetUserId === viewerUserId;

  const isHr = Roles.HR.includes(viewerRole);
  const isManagerRole = Roles.MANAGER.includes(viewerRole);
  const [isManagerOfTarget, setIsManagerOfTarget] = useState(false);

  useEffect(() => {
    if (!targetUserId || !viewerUserId) {
      setIsManagerOfTarget(false);
      return;
    }
    if (isSelf) {
      setIsManagerOfTarget(false);
      return;
    }
    if (!isManagerRole) {
      setIsManagerOfTarget(false);
      return;
    }

    const check = async () => {
      try {
        const res = await getUserManagers(targetUserId);
        const managers = res?.payload || [];
        const ok = managers.some((s) => String(s?.id) === String(viewerUserId));
        setIsManagerOfTarget(ok);
      } catch {
        setIsManagerOfTarget(false);
      }
    };

    check();
  }, [targetUserId, viewerUserId, isSelf, isManagerRole]);

  const canViewTarget = isSelf || isHr || isManagerOfTarget;
  const canActOnTarget = isSelf;

  const [monthDayKeys, setMonthDayKeys] = useState([]);
  const [summaryByDate, setSummaryByDate] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [correctionRequests, setCorrectionRequests] = useState([]);
  const [correctionsLoading, setCorrectionsLoading] = useState(false);

  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [monthLoading, setMonthLoading] = useState(false);
  const [monthError, setMonthError] = useState(null);
  const [clockingsByDate, setClockingsByDate] = useState({});

  const [companyRules, setCompanyRules] = useState(null);
  // Bumped by the error-state retry button to re-run the month fetch.
  const [monthReloadKey, setMonthReloadKey] = useState(0);

  const uiRole = (isHr || isManagerRole) ? 'manager' : 'employee';
  const isViewingByProp = !!targetUserIdProp;
  const isViewingByRoute = !!targetUserIdFromRoute;
  const title = (!isViewingByProp && !isViewingByRoute) || isSelf
    ? t('My Attendance')
    : t("{{name}}'s Attendance", { name: effectiveTargetUserFullName || t('User') });

  useEffect(() => {
    if (!viewerCompanyId) {
      setCompanyRules(null);
      return;
    }

    const load = async () => {
      try {
        const res = await getCompanyRules(viewerCompanyId);
        setCompanyRules(res?.payload || null);
      } catch {
        setCompanyRules(null);
      }
    };

    load();
  }, [viewerCompanyId]);
  
  useEffect(() => {
    if (!targetUserId) return;
    if (!canViewTarget) return;

    const fetchMonth = async () => {
      setMonthLoading(true);
      setMonthError(null);
      try {
        const from = startOfMonth(currentMonth);
        const to = endOfMonth(currentMonth);
        const fromParam = formatLocalDateParam(from);
        const toParam = formatLocalDateParam(to);

        const res = await getTimesheetTableData(targetUserId, fromParam, toParam);
        const payload = res?.payload;

        const days = eachDayOfInterval({ start: from, end: to });
        const summaries = payload?.daysSummaryView || [];
        const daysClockings = payload?.daysClockings || [];

        const nextClockingsByDate = {};
        const nextSummaryByDate = {};
        const nextMonthDayKeys = [];

        days.forEach((dateObj, idx) => {
          const dateStr = formatLocalDateParam(dateObj);
          const summary = summaries[idx];
          const clockings = daysClockings[idx] || [];

          nextMonthDayKeys.push(dateStr);
          nextSummaryByDate[dateStr] = summary;

          nextClockingsByDate[dateStr] = clockings;
        });

        setMonthDayKeys(nextMonthDayKeys);
        setSummaryByDate(nextSummaryByDate);
        setClockingsByDate(nextClockingsByDate);
      } catch {
        setMonthError(t("We couldn't load this month's attendance."));
      } finally {
        setMonthLoading(false);
      }
    };

    fetchMonth();
  }, [targetUserId, canViewTarget, currentMonth, monthReloadKey, t]);

  const monthRange = useMemo(() => {
    const from = startOfMonth(currentMonth);
    const to = endOfMonth(currentMonth);
    return eachDayOfInterval({ start: from, end: to });
  }, [currentMonth]);

  const { shifts: monthScheduledShifts } = useScheduledShifts(monthRange, {
    userId: canViewTarget ? targetUserId : (viewerUserId || undefined),
  });

  const attendanceData = useMemo(() => {
    const todayKey = formatLocalDateParam(new Date());
    const rules = companyRules || {};

    const absentThresholdPercent = Number.isFinite(Number(rules?.scheduledShiftAbsentThresholdPercent))
      ? Number(rules.scheduledShiftAbsentThresholdPercent)
      : 10;
    const halfDayThresholdPercent = Number.isFinite(Number(rules?.scheduledShiftHalfDayThresholdPercent))
      ? Number(rules.scheduledShiftHalfDayThresholdPercent)
      : 50;
    const lateThresholdMinutes = Number.isFinite(Number(rules?.scheduledShiftLateClockInThresholdMinutes))
      ? Number(rules.scheduledShiftLateClockInThresholdMinutes)
      : 5;
    const earlyThresholdMinutes = Number.isFinite(Number(rules?.scheduledShiftEarlyClockOutThresholdMinutes))
      ? Number(rules.scheduledShiftEarlyClockOutThresholdMinutes)
      : 5;

    const toMinutes = (hhmm) => {
      if (!hhmm || typeof hhmm !== 'string') return null;
      const [hh, mm] = hhmm.split(':');
      const h = Number(hh);
      const m = Number(mm);
      if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
      return h * 60 + m;
    };

    const parseDurationToMinutes = (duration) => {
      if (!duration || typeof duration !== 'string') return 0;
      const parts = duration.split(':');
      if (parts.length < 2) return 0;
      const h = Number(parts[0]);
      const m = Number(parts[1]);
      const s = Number(parts[2] ?? 0);
      if (!Number.isFinite(h) || !Number.isFinite(m) || !Number.isFinite(s)) return 0;
      return h * 60 + m + Math.round(s / 60);
    };

    const pickClock = (clockings, type, pickLast) => {
      const list = (clockings || []).filter((c) => c?.type === type);
      if (list.length === 0) return null;
      const item = pickLast ? list[list.length - 1] : list[0];
      const value = item?.time;
      if (!value) return null;
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return null;
      const hh = String(d.getHours()).padStart(2, '0');
      const mm = String(d.getMinutes()).padStart(2, '0');
      return `${hh}:${mm}`;
    };

    // Consolidate scheduled shifts by date (earliest start, latest end)
    const schedByDate = {};
    (monthScheduledShifts || []).forEach((s) => {
      if (!s?.date) return;
      const start = toMinutes(s.startTime);
      const end = toMinutes(s.endTime);
      if (start == null || end == null) return;

      const current = schedByDate[s.date];
      const next = {
        startMinutes: current ? Math.min(current.startMinutes, start) : start,
        endMinutes: current ? Math.max(current.endMinutes, end) : end,
      };

      // handle shifts that cross midnight (rare, but safe)
      const rawDuration = next.endMinutes - next.startMinutes;
      next.durationMinutes = rawDuration >= 0 ? rawDuration : (24 * 60 + rawDuration);
      schedByDate[s.date] = next;
    });

    // The roster's own word for the day, before any attendance evidence is
    // taken into account. `null` means the backend had nothing to say.
    const mapNominalDayStatus = (dayStatus) => {
      switch (dayStatus) {
        case 'Present':
          return 'present';
        case 'HalfDayLeave':
          return 'half-day';
        case 'Leave':
          return 'leave';
        case 'SickLeave':
          return 'sick-leave';
        case 'Holiday':
        case 'PublicHoliday':
          return 'holiday';
        // Without this case a week-off day mapped to null and was dropped from
        // `rows` entirely, so the web calendar silently omitted it while mobile
        // showed it. See DayStatus.cs / models/attendance.ts.
        // NOTE: the backend emits WeekOffDay for *any* day no scheduled shift
        // covers — i.e. every day of every tenant that has not configured work
        // schedules. It is a nominal label, never proof the day was not worked.
        case 'WeekOffDay':
          return 'week-off';
        default:
          return null;
      }
    };

    const clampPercent = (value, fallback) => {
      const v = Number(value);
      if (!Number.isFinite(v)) return fallback;
      return Math.min(100, Math.max(0, v));
    };

    const absentPct = clampPercent(absentThresholdPercent, 10);
    const halfDayPct = clampPercent(halfDayThresholdPercent, 50);
    const absentRatio = absentPct / 100;
    const halfDayRatio = halfDayPct / 100;

    const rows = [];
    (monthDayKeys || []).forEach((dateStr) => {
      const summary = summaryByDate?.[dateStr];
      const clockings = clockingsByDate?.[dateStr] || [];
      const sched = schedByDate?.[dateStr];
      const isFuture = !!dateStr && dateStr > todayKey;

      const workingMinutes = parseDurationToMinutes(summary?.workingTime);
      const hoursWorked = workingMinutes / 60;

      const nominalStatus = mapNominalDayStatus(summary?.dayStatus);
      const hasClockings = clockings.length > 0;
      const isNonWorkingDay = NON_WORKING_STATUSES.has(nominalStatus);

      // ── Precedence ─────────────────────────────────────────────────────────
      // Highest wins. Explicit, so no future edit can make it an accident of
      // truthiness again (a `case 'WeekOffDay'` added to the map above once
      // made every worked day of a schedule-less tenant read "Week Off"):
      //   1. Clockings exist            → worked (present, refined below)
      //   2. Leave / sick leave         → leave / sick-leave
      //   3. Holiday                    → holiday
      //   4. Week off                   → week-off
      //   5. Rostered, nobody clocked   → absent (past days only)
      //   6. Nothing at all             → the day is not rendered
      // 2–4 are mutually exclusive — the backend emits one DayStatus — so they
      // resolve in a single branch.
      let status;

      if (hasClockings) {
        // 1. Real attendance evidence outranks the roster's label. A worked
        //    day is never downgraded to a nominal one; a nominal status that
        //    already means "worked" is kept as the more specific answer.
        status = WORKED_STATUSES.has(nominalStatus) ? nominalStatus : 'present';
      } else if (nominalStatus) {
        // 2–4. Nobody worked, so the company's own declaration stands.
        status = nominalStatus;
      } else if (sched) {
        // 5. On the roster with nothing recorded. A future shift has not been
        //    missed yet, so it shows as an expected working day instead.
        status = isFuture ? 'present' : 'absent';
      } else {
        // 6. Nothing scheduled, worked or declared — there is nothing to say.
        return;
      }

      if (!isFuture) {
        // Refinements below only ever apply to a day the employee was expected
        // to work AND actually worked. Leave, sick leave, holiday and week off
        // are never refined into absent/half-day/late — the employee owed no
        // hours, so a low ratio is not a shortfall.
        const refinable = WORKED_STATUSES.has(status) && !isNonWorkingDay;

        // Worked less of the scheduled shift than the company's thresholds
        // allow → absent / half day.
        if (refinable && sched && sched.durationMinutes > 0)
        {
          const ratio = workingMinutes / sched.durationMinutes;
          if (ratio <= absentRatio)
          {
            status = 'absent';
          }
          else if (ratio < halfDayRatio)
          {
            status = 'half-day';
          }
        }

        // Late / early leave indicators (only when the day still reads as worked)
        if (refinable && sched && WORKED_STATUSES.has(status))
        {
          const checkIn = pickClock(clockings, 'ClockIn', false);
          const checkOut = pickClock(clockings, 'ClockOut', true);
          let checkInM = toMinutes(checkIn);
          let checkOutM = toMinutes(checkOut);

          const startM = sched.startMinutes;
          let endM = sched.endMinutes;
          const crossesMidnight = startM > endM;

          if (crossesMidnight) {
            // Normalize: shift times on the "next day" side (0..endM) get +24h
            // so all comparisons are on a continuous 0..2880 scale
            const rawEndM = endM;
            endM += 24 * 60;
            if (checkInM != null && checkInM <= rawEndM) {
              checkInM += 24 * 60;
            }
            if (checkOutM != null && checkOutM <= rawEndM) {
              checkOutM += 24 * 60;
            }
          }

          if (checkInM != null && checkInM > (startM + Math.max(0, lateThresholdMinutes)))
          {
            status = 'late';
          }
          else if (checkOutM != null && checkOutM < (endM - Math.max(0, earlyThresholdMinutes)))
          {
            status = 'early-leave';
          }
        }
      }

      rows.push({
        date: dateStr,
        status,
        // What the roster said before evidence was applied, so a day worked on
        // a week off or a holiday can still say so, and so the KPI strip can
        // tell a genuinely non-working day from a worked one.
        nominalStatus,
        isNonWorkingDay,
        workedOnNonWorkingDay: isNonWorkingDay && hasClockings,
        hasClockings,
        hoursWorked: Number(hoursWorked.toFixed(2)),
        checkIn: pickClock(clockings, 'ClockIn', false),
        checkOut: pickClock(clockings, 'ClockOut', true),
        breakIn: pickClock(clockings, 'BreakIn', false),
        breakOut: pickClock(clockings, 'BreakOut', true),
        dayStatus: summary?.dayStatus,
      });
    });

    return rows;
  }, [monthDayKeys, summaryByDate, clockingsByDate, monthScheduledShifts, companyRules, canViewTarget, targetUserId, viewerUserId]);

  useEffect(() => {
    if (!viewerUserId) return;
    if (!isSelf) {
      setCorrectionRequests([]);
      return;
    }

    const fetchMyCorrections = async () => {
      setCorrectionsLoading(true);
      try {
        const res = await getMyCorrectionRequests(viewerUserId);
        setCorrectionRequests(res?.payload || []);
      } catch {
        setCorrectionRequests([]);
      } finally {
        setCorrectionsLoading(false);
      }
    };

    fetchMyCorrections();
  }, [viewerUserId, isSelf]);

  const handleCorrectionRequest = () => {
    const prefillDate = selectedDate || formatLocalDateParam(new Date());
    navigate('/attendance/correction-requests', {
      state: {
        prefill: {
          date: prefillDate,
        },
      },
    });
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
  };

  const handleExportReport = () => {
    navigate('/attendance/reports/clockings');
  };

  // The four header tiles read from the SAME resolved status as the calendar
  // cell and the day panel — never from the raw DayStatus — so the page cannot
  // contradict itself (a day showing two sessions and 6.92 hours used to be
  // counted as 0 present days).
  const stats = useMemo(() => {
    const days = attendanceData || [];
    const totalDays = days.length;
    const presentDays = days.filter(day => PRESENT_STATUSES.has(day?.status)).length;
    const absentDays = days.filter(day => ABSENT_STATUSES.has(day?.status)).length;
    const halfDays = days.filter(day => day?.status === 'half-day').length;
    // Week-off and holidays are present in the rows (they used to be silently
    // dropped). A day nobody worked is not a working day, so it stays out of
    // the rate entirely. A week off the employee DID work counts on both sides
    // of the ratio: it can neither be hidden nor inflate the percentage.
    const nonWorkingDays = days.filter(day => day?.isNonWorkingDay && !day?.hasClockings).length;
    const workingDays = Math.max(0, totalDays - nonWorkingDays);
    const attendanceRate = workingDays > 0 ? ((presentDays + halfDays * 0.5) / workingDays * 100)?.toFixed(1) : 0;
    return { totalDays, presentDays, absentDays, halfDays, attendanceRate };
  }, [attendanceData]);

  const getCorrectionRequestStatusLabel = (status) => {
    const s = String(status || '').toLowerCase();
    if (s === 'approved') return t('Approved');
    if (s === 'rejected') return t('Rejected');
    if (s === 'pending') return t('Pending');
    return status ? t(String(status)) : '';
  };

  const selectedDateDetails = useMemo(() => {
    if (!selectedDate) return null;
    return attendanceData?.find(day => day?.date === selectedDate) || null;
  }, [attendanceData, selectedDate]);

  // The selected day's clockings as ordered SESSIONS rather than four type
  // buckets. A session opens on ClockIn/StartTask and closes on ClockOut/StopTask;
  // breaks belong to the session that encloses them. Split shifts therefore read
  // as "09:00 – 12:00" and "14:00 – 18:00" instead of two columns the reader has
  // to re-pair mentally. Every field the API already returns is carried through
  // (address, comments, isEdited, clientType, imageUrl) — no extra request:
  // TimesheetTableData.daysClockings already holds all of it.
  const selectedDaySessions = useMemo(() => {
    if (!selectedDate) return [];

    const OPENERS = new Set(['ClockIn', 'StartTask']);
    const CLOSERS = new Set(['ClockOut', 'StopTask']);
    const KNOWN = new Set(['ClockIn', 'ClockOut', 'BreakIn', 'BreakOut', 'StartTask', 'StopTask']);

    const hm = (d) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

    const entries = (clockingsByDate?.[selectedDate] || [])
      .filter((c) => KNOWN.has(c?.type) && c?.time)
      .map((c, idx) => {
        const d = new Date(c.time);
        return { raw: c, idx, date: d };
      })
      .filter((x) => !Number.isNaN(x.date.getTime()))
      .sort((a, b) => (a.date.getTime() - b.date.getTime()) || (a.idx - b.idx))
      .map((x) => ({
        key: x.raw?.id != null ? `c-${x.raw.id}` : `c-${x.date.getTime()}-${x.idx}`,
        type: x.raw.type,
        date: x.date,
        timeLabel: hm(x.date),
        address: x.raw.address || null,
        comments: x.raw.comments || null,
        isEdited: !!x.raw.isEdited,
        clientType: x.raw.clientType || null,
        // AttendanceService nulls this out when CompanyRules.HideClockingImagesFromTimesheet
        // is set, so an absent photo must render as nothing at all — never an empty frame.
        imageUrl: x.raw.imageUrl || null,
      }));

    const sessions = [];
    let current = null;

    const close = () => {
      if (current && current.entries.length > 0) sessions.push(current);
      current = null;
    };

    entries.forEach((e) => {
      if (OPENERS.has(e.type)) {
        // A new opener always begins a session; an unclosed one is flushed first
        // (happens when a clock-out was never recorded).
        close();
        current = { key: `s-${e.key}`, entries: [e], start: e, end: null };
        return;
      }
      if (!current) {
        // Orphan closer/break (e.g. a clock-out belonging to a shift that started
        // the previous day). Show it rather than dropping it.
        current = { key: `s-${e.key}`, entries: [], start: null, end: null };
      }
      current.entries.push(e);
      if (CLOSERS.has(e.type)) {
        current.end = e;
        close();
      }
    });
    close();

    return sessions.map((s) => {
      let durationLabel = null;
      if (s.start && s.end) {
        const mins = Math.max(0, Math.round((s.end.date.getTime() - s.start.date.getTime()) / 60000));
        const h = Math.floor(mins / 60);
        const m = mins % 60;
        durationLabel = h > 0 ? t('{{h}}h {{m}}m', { h, m }) : t('{{m}}m', { m });
      }
      return {
        key: s.key,
        entries: s.entries,
        startLabel: s.start?.timeLabel || null,
        endLabel: s.end?.timeLabel || null,
        durationLabel,
      };
    });
  }, [selectedDate, clockingsByDate, t]);

  const getUiStatusLabel = (status) => {
    switch (status) {
      case 'present':
        return t('Present');
      case 'absent':
        return t('Absent');
      case 'leave':
        return t('Leave');
      case 'sick-leave':
        return t('Sick leave');
      case 'holiday':
        return t('Holiday');
      case 'half-day':
        return t('Half day');
      case 'late':
        return t('Late');
      case 'early-leave':
        return t('Early Clock out');
      case 'week-off':
        return t('Week Off');
      default:
        return status ? String(status) : '';
    }
  };

  // Design tokens, not raw Tailwind palette steps: bg-green-100/text-green-800
  // stayed light-on-light in dark mode. The /10 tint + solid token text reads in
  // both themes and matches the badge vocabulary used elsewhere in the app.
  const getUiStatusBadgeClass = (status) => {
    switch (status) {
      case 'present':
        return 'bg-success/10 text-success';
      case 'absent':
        return 'bg-destructive/10 text-destructive';
      case 'sick-leave':
        return 'bg-destructive/10 text-destructive';
      case 'leave':
        return 'bg-warning/10 text-warning';
      case 'late':
        return 'bg-warning/10 text-warning';
      case 'half-day':
        return 'bg-secondary/10 text-secondary';
      case 'holiday':
        return 'bg-secondary/10 text-secondary';
      case 'early-leave':
        return 'bg-primary/10 text-primary';
      case 'week-off':
        return 'bg-muted text-muted-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };


  if (!canViewTarget) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-3xl mx-auto px-6 py-10">
          <div className="bg-card border border-border rounded-xl p-6">
            <h1 className="text-xl font-semibold text-foreground">{t('Not authorized')}</h1>
            <p className="text-sm text-muted-foreground mt-2">{t("You don't have access to view this user's attendance.")}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <PageShell
      title={title}
      description={uiRole === 'manager' ? t('Monitor attendance and manage schedules') : t('Track your attendance and manage time-off requests')}
      icon={Calendar}
      accentColor="blue"
      maxWidth="1600px"
      stats={[
        { label: t('Attendance Rate'), value: `${stats?.attendanceRate}%`, color: 'text-green-600' },
        { label: t('Present Days'), value: stats?.presentDays },
        { label: t('Absent Days'), value: stats?.absentDays, color: 'text-orange-600' },
        { label: t('Half Days'), value: stats?.halfDays, color: 'text-blue-600' },
      ]}
    >
      <div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Calendar and Upcoming Schedule */}
          <div className="lg:col-span-2 space-y-6">
            {/* Export Button and Calendar View */}
            <div className="bg-card border border-border rounded-xl">
              <div className="p-6 border-b border-border">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="text-xl font-semibold text-foreground flex items-center">
                      <Icon name="Calendar" size={20} className={titleIconGapClass} />
                      {t('Attendance Calendar')}
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      {t('Visual overview of attendance patterns and scheduled time-off')}
                    </p>
                  </div>
                  <div className={`flex shrink-0 items-center ${inlineSpaceClass}`}>
                      {uiRole === 'manager' && (
                        <button
                          type="button"
                          onClick={handleExportReport}
                          className={`px-3 py-2 text-sm bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground rounded-md transition-colors flex items-center ${inlineSpaceClass}`}
                        >
                          <Icon name="Download" size={14} />
                          <span>{t('Export Report')}</span>
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={handleCorrectionRequest}
                        disabled={!canActOnTarget}
                        className={`px-3 py-2 text-sm bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground rounded-md transition-colors flex items-center disabled:opacity-50 ${inlineSpaceClass}`}
                      >
                        <Icon name="Edit3" size={14} />
                        <span>{t('Correction Request')}</span>
                      </button>
                  </div>
                </div>
              </div>
              <div className="p-6">
                {monthLoading && monthDayKeys.length === 0 ? (
                  <CalendarSkeleton />
                ) : (
                  <AttendanceCalendar
                    attendanceData={attendanceData}
                    month={currentMonth}
                    onMonthChange={setCurrentMonth}
                    onDateSelect={handleDateSelect}
                    selectedDate={selectedDate}
                  />
                )}
                {monthLoading && monthDayKeys.length > 0 && (
                  <div className={`mt-4 inline-flex items-center text-sm text-muted-foreground ${inlineSpaceClass}`} aria-live="polite">
                    <Skeleton className="h-2 w-2 rounded-full" />
                    <span>{t('Loading month...')}</span>
                  </div>
                )}
                {monthError && (
                  <div
                    role="alert"
                    className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3"
                  >
                    <div className="flex items-start gap-2 min-w-0">
                      <Icon name="AlertTriangle" size={16} className="mt-0.5 shrink-0 text-destructive" />
                      <p className="text-sm text-destructive">{monthError}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setMonthReloadKey((k) => k + 1)}
                      disabled={monthLoading}
                      className={`inline-flex items-center gap-2 rounded-md border border-destructive/40 px-3 py-1.5 text-sm font-medium text-destructive transition-colors hover:bg-destructive/10 disabled:opacity-50`}
                    >
                      <Icon name="RefreshCw" size={14} />
                      <span>{t('Try again')}</span>
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Work Schedule (moved from timesheet/workSchedule.tsx) */}
            <div className="bg-card border border-border rounded-xl">
              <div className="p-6 border-b border-border">
                <h2 className="text-xl font-semibold text-foreground flex items-center">
                  <Icon name="Calendar" size={20} className={titleIconGapClass} />
                  {uiRole === 'manager' ? t('Work Schedule') : t('My Work Schedule')}
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {uiRole === 'manager' ? t('View scheduled shifts (week/month)') : t('Your scheduled shifts (week/month)')}
                </p>
              </div>
              <div className="p-6">
                <WorkScheduleCalendar userId={canViewTarget ? targetUserId : (viewerUserId || undefined)} />
              </div>
            </div>


          </div>

          {/* Right Column - Detailed Day View, Leave Requests, and Approval Status */}
          <div className="space-y-6">
            <DetailedDayViewPanel
              selectedDate={selectedDate}
              selectedDateDetails={selectedDateDetails}
              sessions={selectedDaySessions}
              getUiStatusBadgeClass={getUiStatusBadgeClass}
              getUiStatusLabel={getUiStatusLabel}
            />

            {/* Leave Request Panel - Moved above Approval Status Tracking */}
            <LeaveRequestPanel
              userId={targetUserId}
              canRequest={isSelf}
              canApprove={isHr || isManagerOfTarget}
            />

            {/* Approval Status Tracking Section - Moved below Leave Request Panel */}
            {isSelf && (
              <div className="bg-card border border-border rounded-xl">
                <div className="p-6 border-b border-border">
                  <h3 className="text-lg font-semibold text-foreground flex items-center">
                    <Icon name="Clock3" size={18} className={titleIconGapClass} />
                    {t('Approval Status Tracking')}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t('Track the status of your attendance correction requests')}
                  </p>
                </div>
                <div className="p-6">
                  {correctionsLoading && correctionRequests.length === 0 ? (
                    <div className="space-y-3" aria-busy="true" aria-live="polite">
                      {[0, 1, 2].map((i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                          <div className={`flex items-center ${rtl ? 'space-x-3 space-x-reverse' : 'space-x-3'}`}>
                            <Skeleton className="w-3 h-3 rounded-full" />
                            <div className="space-y-2">
                              <Skeleton className="h-4 w-40 rounded" />
                              <Skeleton className="h-3 w-56 rounded" />
                            </div>
                          </div>
                          <Skeleton className="h-5 w-20 rounded-full" />
                        </div>
                      ))}
                    </div>
                  ) : correctionRequests?.length > 0 ? (
                    <div className="space-y-3">
                      {correctionRequests.slice(0, 3).map((request) => (
                        <div key={request?.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                          <div className={`flex items-center ${rtl ? 'space-x-3 space-x-reverse' : 'space-x-3'}`}>
                            <div className={`w-3 h-3 rounded-full ${
                              request?.status === 'approved' ? 'bg-green-500' :
                              request?.status === 'rejected' ? 'bg-red-500' : 'bg-yellow-500'
                            }`}></div>
                            <div>
                              <div className="text-sm font-medium text-foreground">
                                {request?.type} - {appDate(new Date(request?.date))}
                              </div>
                              <div className="text-xs text-muted-foreground">{request?.reason}</div>
                            </div>
                          </div>
                          <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                            request?.status === 'approved' ? 'bg-green-100 text-green-800' :
                            request?.status === 'rejected'? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {getCorrectionRequestStatusLabel(request?.status)}
                          </div>
                        </div>
                      ))}

                      {correctionRequests.length > 3 && (
                        <button
                          type="button"
                          onClick={() => navigate('/attendance/correction-requests')}
                          className={`w-full flex items-center justify-center ${inlineSpaceClass} px-3 py-2 text-sm font-medium text-primary border border-border rounded-lg hover:bg-muted transition-colors`}
                        >
                          <span>{t('View all ({{n}})', { n: correctionRequests.length })}</span>
                          <Icon name={rtl ? 'ChevronLeft' : 'ChevronRight'} size={14} />
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Icon name="CheckCircle2" size={48} className="mx-auto mb-3 opacity-50" />
                      <p>{t('No correction requests found')}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default AttendanceManagement;